// botDetector.js
// Client-side bot detection utility for filtering automated traffic from analytics
// Mirrors server-side BotDetector patterns with additional browser environment checks

/**
 * Client-side bot detection utility for filtering automated traffic from analytics
 */
export class BotDetector {
  // User agent patterns matching server-side BotDetector
  // TODO: Make bot patterns configurable via environment variable
  // e.g., BOT_DETECTION_PATTERNS="googlebot,bingbot,crawler"
  static BOT_PATTERNS = [
    // Search engines - honest identification, won't spoof
    /googlebot/i,
    /bingbot/i,
    /slurp/i, // Yahoo
    /duckduckbot/i,
    /baiduspider/i,
    /yandexbot/i,

    // Social media crawlers - honest identification
    /facebookexternalhit/i,
    /twitterbot/i,
    /linkedinbot/i,
    /whatsapp/i,

    // Generic bot patterns - clear automation signals
    /crawler/i,
    /spider/i,
    /scraper/i,
    /bot[\s\-_]/i,
    /[\s\-_]bot/i,

    // Headless browsers - clear automation indicators
    /headlesschrome/i,
    /phantomjs/i,
    /selenium/i,
    /puppeteer/i,

    // Commented out for emergency deployment - easily spoofed and breaks internal tooling
    // TODO: Re-evaluate these patterns after implementing env var configuration
    // /pingdom/i,
    // /uptimerobot/i,
    // /uptime-kuma/i,
    // /newrelic/i,
    // /datadog/i,
    // /curl/i,
    // /wget/i,
    // /python/i,
    // /node/i,
    // /axios/i,
  ];

  /**
   * Check if user agent indicates bot traffic
   * @param {string} userAgent - User agent string to check
   * @returns {boolean} True if user agent matches bot patterns
   */
  static isBotUserAgent(userAgent = navigator.userAgent) {
    if (!userAgent) return true; // No user agent = likely bot
    
    return this.BOT_PATTERNS.some((pattern) => pattern.test(userAgent));
  }

  /**
   * Detect headless browser environment indicators
   * @returns {boolean} True if environment suggests headless browser
   */
  static isHeadlessBrowser() {
    if (typeof window === 'undefined') return true;

    // Check for WebDriver automation
    if (navigator.webdriver === true) return true;

    // Check for missing capabilities that indicate headless environment
    const checks = [
      // No touch support (suspicious for mobile environments)
      !('ontouchstart' in window),
      
      // Missing mobile device APIs
      !window.DeviceOrientationEvent,
      !window.DeviceMotionEvent,
      
      // Suspicious screen dimensions
      screen.width === 0 || screen.height === 0,
      screen.colorDepth === 0,
      
      // No plugins (common in headless)
      navigator.plugins.length === 0,
      
      // Empty languages array
      navigator.languages.length === 0,
      
      // Missing expected browser features
      !window.chrome && /Chrome/.test(navigator.userAgent), // Chrome without chrome object
    ];

    // If multiple indicators are present, likely headless
    const suspiciousCount = checks.filter(Boolean).length;
    return suspiciousCount >= 3;
  }

  /**
   * Check for suspicious browser environment
   * @returns {boolean} True if environment suggests automation
   */
  static isSuspiciousEnvironment() {
    if (typeof window === 'undefined') return true;

    try {
      // Check for automation frameworks
      if (window.callPhantom || 
          window._phantom || 
          window.phantom ||
          window.__nightmare ||
          window.domAutomation ||
          window.domAutomationController) {
        return true;
      }

      // Check for missing expected properties
      if (!navigator.userAgent || 
          !navigator.language || 
          typeof navigator.permissions === 'undefined') {
        return true;
      }

      // Canvas fingerprinting - headless browsers often have different rendering
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Bot detection test', 2, 2);
      const canvasData = canvas.toDataURL();
      
      // Known headless browser canvas signatures (simplified check)
      if (canvasData.length < 100) return true;

      return false;
    } catch (error) {
      // If we can't perform checks, assume it's suspicious
      return true;
    }
  }

  /**
   * Get bot type category (similar to server-side implementation)
   * @param {string} userAgent - User agent string to analyze
   * @returns {string} Bot type category
   */
  static getBotType(userAgent = navigator.userAgent) {
    if (!userAgent) return 'no-user-agent';

    const ua = userAgent.toLowerCase();

    if (ua.includes('googlebot')) return 'googlebot';
    if (ua.includes('bingbot')) return 'bingbot';
    if (ua.includes('facebookexternalhit')) return 'facebook';
    if (ua.includes('twitterbot')) return 'twitter';
    if (ua.includes('uptime-kuma') || ua.includes('pingdom') || ua.includes('uptimerobot')) {
      return 'monitoring';
    }
    if (ua.includes('crawler') || ua.includes('spider')) return 'crawler';
    if (ua.includes('bot')) return 'generic-bot';
    if (ua.includes('headless') || ua.includes('phantom') || ua.includes('selenium')) {
      return 'headless-browser';
    }
    if (ua.includes('curl') || ua.includes('wget') || ua.includes('python') || 
        ua.includes('node') || ua.includes('axios')) {
      return 'script';
    }

    // Check environment-based detection
    if (this.isHeadlessBrowser()) return 'headless-environment';
    if (this.isSuspiciousEnvironment()) return 'suspicious-environment';

    return 'unknown-bot';
  }

  /**
   * Main bot detection method - combines all checks
   * @returns {boolean} True if request appears to be from a bot
   */
  static isBot() {
    // Quick user agent check first
    if (this.isBotUserAgent()) return true;
    
    // Environment-based checks
    if (this.isHeadlessBrowser()) return true;
    if (this.isSuspiciousEnvironment()) return true;

    return false;
  }

  /**
   * Get comprehensive bot detection results
   * @returns {object} Detailed bot detection results
   */
  static getDetectionResults() {
    const userAgent = navigator.userAgent;
    const isBotUA = this.isBotUserAgent(userAgent);
    const isHeadless = this.isHeadlessBrowser();
    const isSuspicious = this.isSuspiciousEnvironment();
    const isBot = isBotUA || isHeadless || isSuspicious;

    return {
      isBot,
      botType: isBot ? this.getBotType(userAgent) : null,
      userAgent,
      checks: {
        userAgentBot: isBotUA,
        headlessBrowser: isHeadless,
        suspiciousEnvironment: isSuspicious
      },
      timestamp: new Date().toISOString()
    };
  }
}

export default BotDetector;
